/**
 * Category chip with inline edit + delete controls.
 *
 * Two interaction modes on the same surface:
 *   • Default click → filter by this category (matches the existing chip behaviour)
 *   • Hover (desktop) / long-press (mobile) → reveals a pencil + trash button
 *
 * Edit pencil → swaps the chip's label for an inline input. Enter saves,
 *               Escape discards.
 * Trash → opens a 2-step confirm: "Delete X items and the category?"
 *
 * Pure presentational — the parent owns the actual rename/delete API call.
 *
 * Props:
 *   - label           string             e.g. "Mains"
 *   - count           number             items currently under this category
 *   - active          bool               highlighted state when filter-selected
 *   - isAllChip       bool               "All" chip: filter only, no edit/delete
 *   - onSelect        () => void         filter handler
 *   - onRename        (next: string) => Promise<void>
 *   - onDelete        () => Promise<void>
 *   - accentColor     CSS color string   e.g. "#00FFFF" or "#9D00FF"
 *   - sortOrder       number             display-order value (0 by default)
 *   - onSortOrderChange (n: number) => Promise<void>   set-order handler; when omitted, the order input is hidden (e.g. for top-level Group chips)
 */
import React, { useState, useRef, useEffect } from 'react';
import { Pencil, Trash2, Check, X, AlertTriangle } from 'lucide-react';

const EditableCategoryChip = ({
  label,
  count = 0,
  active = false,
  isAllChip = false,
  onSelect,
  onRename,
  onDelete,
  accentColor = '#00FFFF',
  sortOrder = 0,
  onSortOrderChange,
}) => {
  const [mode, setMode] = useState('view'); // 'view' | 'edit' | 'confirmDelete'
  const [draft, setDraft] = useState(label);
  const [orderDraft, setOrderDraft] = useState(sortOrder);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (mode === 'edit') {
      setDraft(label);
      setOrderDraft(sortOrder);
      // defer so input is mounted before focusing
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 30);
    }
  }, [mode, label, sortOrder]);

  // ── View mode (default chip) ────────────────────────────────────
  if (mode === 'view') {
    return (
      <div
        className="group relative flex-shrink-0 inline-flex"
        data-testid={`category-chip-wrap-${label}`}
      >
        <button
          onClick={onSelect}
          data-testid={`category-chip-${label}`}
          className={`px-3 h-7 rounded-full text-[11px] font-semibold tracking-wide transition-colors ${
            active
              ? 'text-black'
              : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
          }`}
          style={active ? { backgroundColor: accentColor } : undefined}
        >
          {label}
          <span className={`ml-1.5 text-[10px] ${active ? 'text-black/60' : 'text-gray-600'}`}>
            {count}
          </span>
          {onSortOrderChange && sortOrder ? (
            <span
              className={`ml-1 inline-flex items-center justify-center min-w-[16px] h-[14px] px-1 rounded-full text-[9px] font-mono ${
                active ? 'bg-black/25 text-black/70' : 'bg-white/10 text-gray-500'
              }`}
              data-testid={`category-chip-order-${label}`}
              title={`Display order: ${sortOrder}`}
            >
              #{sortOrder}
            </span>
          ) : null}
        </button>

        {/* Edit/delete controls — hidden for the synthetic "All" chip
            (you can't rename/delete "All"). */}
        {!isAllChip && (
          <div className="hidden group-hover:flex items-center gap-0.5 ml-1">
            <button
              onClick={(e) => { e.stopPropagation(); setMode('edit'); }}
              data-testid={`category-edit-${label}`}
              title={`Rename "${label}"`}
              aria-label={`Rename ${label}`}
              className="h-7 w-7 inline-flex items-center justify-center rounded-full bg-white/5 text-gray-400 hover:bg-white/15 hover:text-white"
            >
              <Pencil className="w-3 h-3" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setMode('confirmDelete'); }}
              data-testid={`category-delete-${label}`}
              title={`Delete "${label}" and ${count} item${count === 1 ? '' : 's'}`}
              aria-label={`Delete ${label}`}
              className="h-7 w-7 inline-flex items-center justify-center rounded-full bg-white/5 text-[#FF3B3B]/80 hover:bg-[#FF3B3B]/15 hover:text-[#FF3B3B]"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>
    );
  }

  // ── Edit mode (inline rename + sort order) ────────────────────────────
  if (mode === 'edit') {
    const submit = async () => {
      const nextName = draft.trim();
      const nextOrder = Number.isFinite(Number(orderDraft)) ? parseInt(orderDraft, 10) : 0;
      const nameChanged = !!nextName && nextName !== label;
      const orderChanged = onSortOrderChange && nextOrder !== (sortOrder || 0);
      if (!nameChanged && !orderChanged) { setMode('view'); return; }
      setSaving(true);
      try {
        if (nameChanged) {
          await onRename(nextName);
        }
        if (orderChanged) {
          // Persist against the RENAMED label if we just renamed, so the
          // backend row lands under the new name.
          await onSortOrderChange(nextOrder, nameChanged ? nextName : label);
        }
        setMode('view');
      } catch {
        // Parent surfaces the toast; we just leave edit mode open
      } finally {
        setSaving(false);
      }
    };
    return (
      <div className="flex-shrink-0 inline-flex items-center gap-1" data-testid={`category-edit-row-${label}`}>
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value.slice(0, 60))}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
            if (e.key === 'Escape') setMode('view');
          }}
          disabled={saving}
          data-testid={`category-edit-input-${label}`}
          className="h-7 px-2.5 text-[11px] bg-black border rounded-full text-white outline-none w-32 font-semibold tracking-wide"
          style={{ borderColor: accentColor }}
        />
        {onSortOrderChange && (
          <div
            className="h-7 inline-flex items-center gap-1 pl-2 pr-1 rounded-full bg-black border"
            style={{ borderColor: accentColor }}
            title="Display order (lower first)"
          >
            <span className="text-[9px] uppercase tracking-[0.2em] text-gray-500 font-['JetBrains_Mono']">Order</span>
            <input
              type="number"
              value={orderDraft}
              onChange={(e) => setOrderDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submit();
                if (e.key === 'Escape') setMode('view');
              }}
              disabled={saving}
              data-testid={`category-edit-order-${label}`}
              className="h-6 w-12 text-[11px] bg-transparent text-white outline-none font-mono text-center [-moz-appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
          </div>
        )}
        <button
          onClick={submit}
          disabled={saving}
          data-testid={`category-edit-save-${label}`}
          className="h-7 w-7 inline-flex items-center justify-center rounded-full text-black disabled:opacity-50"
          style={{ backgroundColor: accentColor }}
          aria-label="Save"
        >
          <Check className="w-3 h-3" />
        </button>
        <button
          onClick={() => setMode('view')}
          disabled={saving}
          data-testid={`category-edit-cancel-${label}`}
          className="h-7 w-7 inline-flex items-center justify-center rounded-full bg-white/5 text-gray-400 hover:bg-white/15 disabled:opacity-50"
          aria-label="Cancel"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    );
  }

  // ── Confirm delete mode ────────────────────────────────────
  const confirmDelete = async () => {
    setSaving(true);
    try {
      await onDelete();
    } finally {
      setSaving(false);
      // parent will re-render with this category removed, so no need to flip mode
    }
  };
  return (
    <div
      className="flex-shrink-0 inline-flex items-center gap-1 px-2 h-7 rounded-full bg-[#FF3B3B]/15 border border-[#FF3B3B]/40"
      data-testid={`category-confirm-row-${label}`}
    >
      <AlertTriangle className="w-3 h-3 text-[#FF3B3B]" />
      <span className="text-[11px] text-white font-semibold whitespace-nowrap">
        Delete &ldquo;{label}&rdquo; + {count} item{count === 1 ? '' : 's'}?
      </span>
      <button
        onClick={confirmDelete}
        disabled={saving}
        data-testid={`category-confirm-delete-${label}`}
        className="h-6 px-2 inline-flex items-center justify-center rounded-full bg-[#FF3B3B] text-white text-[10px] font-bold uppercase tracking-wider disabled:opacity-50"
      >
        {saving ? '…' : 'Delete'}
      </button>
      <button
        onClick={() => setMode('view')}
        disabled={saving}
        data-testid={`category-confirm-cancel-${label}`}
        className="h-6 px-2 inline-flex items-center justify-center rounded-full bg-white/10 text-white text-[10px] font-bold uppercase tracking-wider disabled:opacity-50"
      >
        Cancel
      </button>
    </div>
  );
};

export default EditableCategoryChip;
